/**
 * Chongli / Zhangjiakou flagship content: the China equivalent of Japan's
 * Furano push (src/data/furanoDeepDive.ts plus the COPY objects inside
 * src/pages/FuranoPage.tsx, FuranoGuidePage.tsx and FuranoFaqPage.tsx).
 *
 * Rendered at /chongli/ (the travel guide and FAQ were merged into it; site-only
 * pages registered through site.config.ts extraRoutes).
 *
 * Status on 2026-10-20: no Chongli resort had announced a 2026-27 opening
 * date. Season dates are 2025-26 actuals and ticket prices are 2025-26 rack
 * and discounted prices in CNY, each labelled. Trail and lift counts come
 * from the 2026-27 tables published by the Chongli ski booking centre
 * (chonglihuaxue.cn). Sources: sites/cn/CONTENT_SOURCES.md.
 */
import type { CnLang } from './types';

export interface DeepDiveSection {
  id: string;
  heading: string;
  body: string;
  bullets?: string[];
}

export interface DeepDiveCopy {
  heading: string;
  intro: string;
  sections: DeepDiveSection[];
}

/** Same role as Japan's FURANO_DEEP_DIVE. */
export const CHONGLI_DEEP_DIVE: Record<CnLang, DeepDiveCopy> = {
  en: {
    heading: 'Planning Chongli: dates, snow, Olympic venues and getting around',
    intro:
      'Chongli is where Beijing goes skiing and where the 2022 Winter Olympics held its snowboard, freestyle, ski jumping, cross-country and biathlon events. Here is how the season runs, what the snow is like, which resort hosted what, and how to move between them once you arrive.',
    sections: [
      {
        id: 'dates',
        heading: 'Opening and closing dates',
        body:
          'Wanlong is usually first to open, in late October, with the others following in early to mid November. Wanlong opened on 21 October 2025, its earliest start in 23 years, and on 23 October 2026. China Tourism News (24 October 2026) expects Genting, Thaiwoo, Fulong and Cuiyunshan to open together in November.',
        bullets: [
          'Wanlong: 21 Oct 2025 to 7 Apr 2026',
          'Genting (Yunding): 1 Nov 2025 to 28 Mar 2026',
          'Fulong and Cuiyunshan: 1 Nov 2025 to 23 Mar 2026',
          'Thaiwoo: 7 Nov 2025 to 23 Mar 2026',
          'Snow Ruyi: 18 Nov 2025 to 23 Mar 2026',
          'Duolemeidi: 31 Dec 2025 to 9 Mar 2026',
        ],
      },
      {
        id: 'snow',
        heading: 'What the snow is like',
        body:
          'Chongli is cold and dry, with little natural snow. The Olympic bid was criticised because the outdoor venues lacked reliable natural snowfall, and the Games used large-scale snowmaking. Expect firm, well-groomed machine snow that holds up all day, with natural top-ups after the occasional storm. Wanlong markets itself as a powder destination, but if deep powder is the goal, Altay in Xinjiang is the better bet.',
      },
      {
        id: 'venues',
        heading: 'The Olympic venues',
        body:
          'The Zhangjiakou cluster of the 2022 Games sat around Taizicheng. Genting Snow Park hosted snowboard and freestyle skiing (except big air) and Paralympic snowboarding, and is a national-team training base. The National Ski Jumping Centre, Snow Ruyi, now runs public skiing on its own runs. The National Cross-Country Centre and National Biathlon Centre were nearby. Alpine skiing was held at the National Alpine Skiing Centre in Yanqing, Beijing, and big air in the city at Shougang. Thaiwoo and Wanlong were not competition venues.',
        bullets: [
          'Genting Snow Park: snowboard, freestyle, Paralympic snowboard',
          'Snow Ruyi (National Ski Jumping Centre): ski jumping, Nordic combined',
          'National Cross-Country Centre and National Biathlon Centre',
          'Yanqing National Alpine Skiing Centre (Beijing): alpine skiing',
        ],
      },
      {
        id: 'rentals',
        heading: 'Rentals and lift tickets',
        body:
          'Every major resort rents skis and boards at the base, and some sell tickets that include rental. At Thaiwoo in 2025-26, the rack price of a weekday lift-plus-rental day was 562 yuan against 443 yuan for the lift only, and the resort took a 2,000-yuan deposit on rented gear. Prices step up at weekends and peak again at New Year and the Spring Festival, and discounted prices through the official channels were much lower than rack.',
        bullets: [
          'Wanlong 2025-26 day ticket: rack 560 weekday / 650 weekend / 800 holiday; discounted 299-699',
          'Genting 2025-26 day ticket: rack 580 weekday / 710 weekend; discounted 288-468',
          'Thaiwoo 2025-26 day ticket: rack 443 weekday / 590 weekend / 756 holiday',
          'Fulong 2025-26 day ticket: 261-320 in November and March, 525-639 December to February',
          'Duolemeidi 2025-26 day ticket: 99-198, the cheapest in Chongli',
        ],
      },
      {
        id: 'localtips',
        heading: 'Getting around and eating',
        body:
          'Taizicheng high-speed station is less than 2 km from Taizicheng Ice and Snow Town, and resorts run free shuttles from the station: Wanlong is about 15 minutes away. Fulong sits on the edge of Chongli town, next to Chongli station. Most people eat at their resort: Wanlong has several themed restaurants and bars, Thaiwoo\'s base village has bars, cafés and an ice rink, and Chongli town has ordinary local restaurants. Most resort hotels are ski-in/ski-out or a short shuttle ride from the lifts.',
        bullets: [
          'Train: Beijing North or Qinghe to Taizicheng or Chongli',
          'Free resort shuttles from Taizicheng station',
          'Hot springs at Thaiwoo, Wanlong and Fulong\'s Tangyin hotels',
          'Non-ski: Snow Ruyi jump hill, Taizicheng Olympic plaza',
        ],
      },
    ],
  },
  zh: {
    heading: '崇礼滑雪规划：雪季、雪质、冬奥场馆与交通',
    intro:
      '崇礼是北京人滑雪的地方，也是2022年冬奥会单板、自由式、跳台滑雪、越野滑雪和冬季两项的举办地。下面介绍雪季安排、雪质特点、各场馆承办过什么比赛，以及到达后如何在雪场之间移动。',
    sections: [
      {
        id: 'dates',
        heading: '开板与闭板日期',
        body:
          '万龙通常最早开板，在10月下旬，其他雪场在11月上中旬陆续开板。万龙2025年10月21日开板，为23年来最早，2026年10月23日再次开板。中国旅游报（2026年10月24日）预计云顶、太舞、富龙、翠云山银河11月集中开板。',
        bullets: [
          '万龙：2025年10月21日、2026年10月23日开板',
          '云顶：2025年11月1日至2026年3月28日',
          '富龙、翠云山：2025年11月1日至2026年3月23日',
          '太舞：2025年11月7日至2026年3月23日',
          '雪如意：2025年11月18日至2026年3月23日',
          '多乐美地：2025年12月31日至2026年3月9日',
        ],
      },
      {
        id: 'snow',
        heading: '雪质特点',
        body:
          '崇礼寒冷干燥，天然降雪不多。冬奥申办时就有人指出户外场地缺乏稳定的天然降雪，赛事大量使用人工造雪。可以预期的是压得很实、整天都不会变烂的人造雪，偶尔下雪后会有天然雪补充。万龙主打"粉雪"宣传，但如果目标是深粉雪，新疆阿勒泰更合适。',
      },
      {
        id: 'venues',
        heading: '冬奥场馆',
        body:
          '2022年冬奥会张家口赛区位于太子城周边。云顶滑雪公园承办单板滑雪和自由式滑雪（大跳台除外）以及冬残奥单板项目，也是国家队训练基地。国家跳台滑雪中心"雪如意"如今有自己的雪道对公众开放。国家越野滑雪中心和国家冬季两项中心也在附近。高山滑雪在北京延庆的国家高山滑雪中心举行，大跳台在市区首钢。太舞和万龙不是比赛场馆。',
        bullets: [
          '云顶滑雪公园：单板、自由式、冬残奥单板',
          '雪如意（国家跳台滑雪中心）：跳台滑雪、北欧两项',
          '国家越野滑雪中心、国家冬季两项中心',
          '延庆国家高山滑雪中心（北京）：高山滑雪',
        ],
      },
      {
        id: 'rentals',
        heading: '租赁与雪票',
        body:
          '各大雪场山脚都提供双板和单板租赁，部分雪场出售含雪具的套票。以太舞2025-26雪季为例，平日含雪具全天票门市价562元，纯雪票443元，租用雪具需交2000元押金。周末票价上调，元旦和春节再次达到高峰；通过官方渠道购买的优惠价远低于门市价。',
        bullets: [
          '万龙2025-26全天票：门市价平日560/周末650/节假日800元；优惠价299-699元',
          '云顶2025-26全天票：门市价平日580/周末710元；优惠价288-468元',
          '太舞2025-26全天票：门市价平日443/周末590/节假日756元',
          '富龙2025-26全天票：11月和3月261-320元，12月至2月525-639元',
          '多乐美地2025-26全天票：99-198元，崇礼最便宜',
        ],
      },
      {
        id: 'localtips',
        heading: '交通与吃饭',
        body:
          '太子城高铁站距太子城冰雪小镇不到2公里，各雪场有从车站出发的免费班车，到万龙约15分钟。富龙位于崇礼城区边缘，紧邻崇礼站。多数人在雪场内吃饭：万龙有多家主题餐厅和酒吧，太舞小镇有酒吧、咖啡馆和滑冰场，崇礼城区则有普通的本地餐馆。大多数雪场酒店可滑进滑出，或坐短途班车即可到达索道。',
        bullets: [
          '火车：北京北站或清河站至太子城或崇礼',
          '太子城站有雪场免费班车',
          '太舞、万龙和富龙汤印酒店有温泉',
          '滑雪之外：雪如意跳台、太子城冬奥广场',
        ],
      },
    ],
  },
  ko: {
    heading: '충리 여행 계획: 시즌·설질·올림픽 경기장·이동',
    intro:
      '충리는 베이징 사람들이 스키를 타러 가는 곳이자 2022년 동계올림픽 스노보드, 프리스타일, 스키점프, 크로스컨트리, 바이애슬론 경기가 열린 곳입니다. 시즌 운영, 눈의 특징, 경기장별 개최 종목, 도착 후 리조트 간 이동을 정리했습니다.',
    sections: [
      {
        id: 'dates',
        heading: '개장과 폐장 일정',
        body:
          '보통 완룽이 10월 말 가장 먼저 열고, 다른 리조트가 11월 초중순에 뒤따릅니다. 완룽은 23년 만에 가장 이른 2025년 10월 21일에 개장했고 2026년 10월 23일에도 문을 열었습니다. 중국여유보(2026년 10월 24일)는 겐팅·타이우·푸룽·추이윈산이 11월에 함께 개장할 것으로 전했습니다.',
        bullets: [
          '완룽: 2025년 10월 21일, 2026년 10월 23일 개장',
          '겐팅(윈딩): 2025년 11월 1일~2026년 3월 28일',
          '푸룽·추이윈산: 2025년 11월 1일~2026년 3월 23일',
          '타이우: 2025년 11월 7일~2026년 3월 23일',
          '쉐루이: 2025년 11월 18일~2026년 3월 23일',
          '둬러메이디: 2025년 12월 31일~2026년 3월 9일',
        ],
      },
      {
        id: 'snow',
        heading: '눈의 특징',
        body:
          '충리는 춥고 건조하며 자연설이 많지 않습니다. 올림픽 유치 당시 야외 경기장에 안정적인 자연설이 부족하다는 비판이 있었고, 대회는 대규모 인공 제설을 썼습니다. 하루 종일 무너지지 않는 단단하고 잘 정설된 인공설을 기대하면 되고, 가끔 눈이 오면 자연설이 더해집니다. 완룽은 파우더를 내세우지만 깊은 파우더가 목적이라면 신장 알타이가 낫습니다.',
      },
      {
        id: 'venues',
        heading: '올림픽 경기장',
        body:
          '2022년 대회 장자커우 클러스터는 타이쯔청 주변에 있었습니다. 겐팅 스노파크는 스노보드와 프리스타일(빅에어 제외), 패럴림픽 스노보드를 치렀고 국가대표 훈련 기지입니다. 국가 스키점프 센터 "쉐루이"는 지금 자체 코스로 일반 스키를 운영합니다. 국가 크로스컨트리 센터와 바이애슬론 센터도 가까이 있습니다. 알파인 스키는 베이징 옌칭의 국가 알파인 스키 센터, 빅에어는 시내 서우강에서 열렸습니다. 타이우와 완룽은 경기장이 아니었습니다.',
        bullets: [
          '겐팅 스노파크: 스노보드, 프리스타일, 패럴림픽 스노보드',
          '쉐루이(국가 스키점프 센터): 스키점프, 노르딕 복합',
          '국가 크로스컨트리 센터, 국가 바이애슬론 센터',
          '옌칭 국가 알파인 스키 센터(베이징): 알파인 스키',
        ],
      },
      {
        id: 'rentals',
        heading: '렌탈과 리프트권',
        body:
          '주요 리조트는 모두 베이스에서 스키와 보드를 빌려 주고, 렌탈 포함 티켓을 파는 곳도 있습니다. 타이우의 2025-26 평일 정가는 리프트+렌탈 562위안, 리프트만 443위안이었고, 렌탈 장비 보증금은 2,000위안이었습니다. 요금은 주말에 오르고 신정과 춘절에 다시 최고가가 되며, 공식 채널 할인가는 정가보다 훨씬 쌌습니다.',
        bullets: [
          '완룽 2025-26 1일권: 정가 평일 560/주말 650/공휴일 800위안, 할인가 299~699위안',
          '겐팅 2025-26 1일권: 정가 평일 580/주말 710위안, 할인가 288~468위안',
          '타이우 2025-26 1일권: 정가 평일 443/주말 590/공휴일 756위안',
          '푸룽 2025-26 1일권: 11월·3월 261~320위안, 12~2월 525~639위안',
          '둬러메이디 2025-26 1일권: 99~198위안, 충리에서 가장 저렴',
        ],
      },
      {
        id: 'localtips',
        heading: '현지 이동과 식사',
        body:
          '타이쯔청 고속철역은 타이쯔청 빙설 타운에서 2km가 안 되고, 리조트들이 역에서 무료 셔틀을 운행합니다. 완룽까지 약 15분입니다. 푸룽은 충리 시내 가장자리, 충리역 옆에 있습니다. 식사는 대부분 리조트 안에서 해결합니다. 완룽에는 테마 레스토랑과 바가 여러 곳 있고, 타이우 베이스 마을에는 바, 카페, 아이스링크가 있으며, 충리 시내에는 평범한 현지 식당이 있습니다. 리조트 호텔 대부분은 스키인·스키아웃이거나 셔틀로 금방입니다.',
        bullets: [
          '기차: 베이징베이역·칭허역에서 타이쯔청 또는 충리',
          '타이쯔청역에서 리조트 무료 셔틀',
          '타이우, 완룽, 푸룽 탕인 호텔에 온천',
          '스키 외: 쉐루이 점프대, 타이쯔청 올림픽 광장',
        ],
      },
    ],
  },
  ja: {
    heading: '崇礼を計画する：日程・雪質・五輪会場・現地の動き方',
    intro:
      '崇礼は北京の人たちがスキーに行く場所であり、2022年冬季五輪のスノーボード、フリースタイル、ジャンプ、クロスカントリー、バイアスロンの会場でもあります。シーズンの流れ、雪の特徴、各会場で行われた競技、到着後のスキー場間の移動をまとめました。',
    sections: [
      {
        id: 'dates',
        heading: 'オープンとクローズの日程',
        body:
          '例年、万龍が10月下旬に最も早くオープンし、ほかは11月上旬から中旬に続きます。万龍は23年ぶりに早い2025年10月21日にオープンし、2026年10月23日にも開きました。中国旅游報（2026年10月24日）は雲頂・太舞・富竜・翠雲山が11月にまとめて開くと伝えています。',
        bullets: [
          '万龍：2025年10月21日、2026年10月23日にオープン',
          '雲頂（ゲンティン）：2025年11月1日〜2026年3月28日',
          '富竜・翠雲山：2025年11月1日〜2026年3月23日',
          '太舞：2025年11月7日〜2026年3月23日',
          '雪如意：2025年11月18日〜2026年3月23日',
          '多楽美地：2025年12月31日〜2026年3月9日',
        ],
      },
      {
        id: 'snow',
        heading: '雪の特徴',
        body:
          '崇礼は寒くて乾燥していて、自然の降雪は多くありません。五輪招致の際、屋外会場には安定した自然降雪がないと批判され、大会は大規模な人工降雪で行われました。期待できるのは、一日中崩れにくい硬めでよく整備された人工雪で、ときどき降る雪が自然に上乗せされます。万龍は「パウダー」を売りにしていますが、深いパウダーが目的なら新疆のアルタイのほうが確実です。',
      },
      {
        id: 'venues',
        heading: '五輪会場',
        body:
          '2022年大会の張家口エリアは太子城周辺にありました。雲頂スノーパークはスノーボードとフリースタイル（ビッグエアを除く）、パラリンピックのスノーボードを開催し、ナショナルチームの練習拠点です。国家ジャンプセンター「雪如意」は現在、独自のコースで一般向けに営業しています。国家クロスカントリーセンターとバイアスロンセンターも近くにあります。アルペンは北京・延慶の国家アルペンスキーセンター、ビッグエアは市内の首鋼で行われました。太舞と万龍は競技会場ではありません。',
        bullets: [
          '雲頂スノーパーク：スノーボード、フリースタイル、パラスノーボード',
          '雪如意（国家ジャンプセンター）：ジャンプ、ノルディック複合',
          '国家クロスカントリーセンター、国家バイアスロンセンター',
          '延慶 国家アルペンスキーセンター（北京）：アルペン',
        ],
      },
      {
        id: 'rentals',
        heading: 'レンタルとリフト券',
        body:
          '主要スキー場はどこもベースでスキーとボードを貸し出し、レンタル込みの券を売るところもあります。太舞の2025-26平日定価はリフト＋レンタル562元、リフトのみ443元で、レンタル用具には2,000元の保証金が必要でした。料金は週末に上がり、元日と春節に再びピークになります。公式チャネルの割引価格は定価よりかなり安くなっていました。',
        bullets: [
          '万龍 2025-26 1日券：定価 平日560／週末650／祝日800元、割引299〜699元',
          '雲頂 2025-26 1日券：定価 平日580／週末710元、割引288〜468元',
          '太舞 2025-26 1日券：定価 平日443／週末590／祝日756元',
          '富竜 2025-26 1日券：11月・3月261〜320元、12〜2月525〜639元',
          '多楽美地 2025-26 1日券：99〜198元、崇礼で最安',
        ],
      },
      {
        id: 'localtips',
        heading: '現地の動き方と食事',
        body:
          '太子城高速鉄道駅は太子城氷雪タウンから2km足らずで、各スキー場が駅から無料シャトルを出しています。万龍まで約15分です。富竜は崇礼の町外れ、崇礼駅のすぐそば。食事はほとんどスキー場内で済ませます。万龍にはテーマレストランやバーがいくつもあり、太舞のベースタウンにはバー、カフェ、スケートリンクがあり、崇礼の町には普通の地元食堂があります。スキー場のホテルの多くはスキーイン・スキーアウトか、シャトルですぐです。',
        bullets: [
          '列車：北京北駅・清河駅から太子城か崇礼へ',
          '太子城駅からスキー場の無料シャトル',
          '太舞、万龍、富竜の湯印ホテルに温泉',
          'スキー以外：雪如意のジャンプ台、太子城の五輪広場',
        ],
      },
    ],
  },
};

// ---------------------------------------------------------------------------
// /chongli/ page copy (Japan: COPY in src/pages/FuranoPage.tsx)
// ---------------------------------------------------------------------------

/** Icon keys used by the page's link list. */
export type ChongliLinkIcon = 'plane' | 'ticket' | 'camera' | 'calendar';

export interface ChongliPageCopy {
  title: string;
  description: string;
  keywords: string;
  h1: string;
  lede: string;
  factsHeading: string;
  facts: { label: string; value: string }[];
  zonesHeading: string;
  zones: { name: string; body: string }[];
  snowHeading: string;
  snowBody: string;
  ticketHeading: string;
  ticketBody: string;
  accessHeading: string;
  accessBody: string;
  linksHeading: string;
  links: { to: string; label: string; icon: ChongliLinkIcon }[];
  faq: { q: string; a: string }[];
}

export const CHONGLI_PAGE: Record<CnLang, ChongliPageCopy> = {
  en: {
    title: 'Chongli ski resort guide 2026-27: resorts, tickets, trains',
    description:
      'Skiing in Chongli, the 2022 Olympic snow venue near Beijing: the main resorts compared, 2025-26 season dates and prices, the train to Taizicheng and where to stay.',
    keywords: 'chongli skiing, chongli ski resort, thaiwoo, genting snow park, wanlong ski resort, fulong, zhangjiakou skiing, beijing skiing',
    h1: 'Chongli ski resorts: season, tickets, trains and a 4-day plan',
    lede:
      'Chongli, in Zhangjiakou, Hebei, is China\'s biggest cluster of ski resorts within reach of Beijing and the snow venue of the 2022 Winter Olympics. The district has nine resorts, 219 runs and 178 km of trails, and the high-speed train from Beijing reaches Taizicheng in about an hour from Qinghe station.',
    factsHeading: 'The numbers',
    facts: [
      { label: 'Location', value: 'Chongli District, Zhangjiakou, Hebei' },
      { label: 'Resorts', value: '9 resorts, 219 runs, 178 km, 16 FIS-certified runs' },
      { label: 'Biggest resorts', value: 'Wanlong 32 runs; Genting 32 trails plus 7 park courses; Fulong has no published run count' },
      { label: 'Season', value: 'Late October to March; Wanlong opened 21 October 2025' },
      { label: 'From Beijing', value: 'High-speed train Qinghe to Taizicheng, about 1 hour' },
    ],
    zonesHeading: 'The main resorts',
    zones: [
      {
        name: 'Wanlong',
        body: 'The skier\'s mountain: 32 runs over about 37 km, 19 of them graded advanced, and the longest run in Chongli at 4,160 m. Top at 2,110 m, about 550 m of vertical, usually first to open.',
      },
      {
        name: 'Genting (Yunding)',
        body: 'The Olympic snowboard and freestyle venue: 32 numbered trails over about 28 km plus seven park and competition courses, a base around 1,700 m and night skiing to 21:30.',
      },
      {
        name: 'Thaiwoo',
        body: 'The most complete base village, with hotels, bars, cafés, hot springs and an ice rink. 22 runs over about 16 km in the 2026-27 table, top near 2,160 m, about 510 m of vertical.',
      },
      {
        name: 'Fulong',
        body: 'Next to Chongli town and its station: 33 runs over about 25 km, a 300,000 m² terrain park and a big kids\' area.',
      },
    ],
    snowHeading: 'Snow and crowds',
    snowBody:
      'Chongli is cold and dry, and its slopes rely on snowmaking: the Olympic bid was criticised for the lack of reliable natural snowfall. The result is a firm, consistent surface with little powder. Weekends and holidays fill with Beijing skiers and the trains sell out, while weekdays outside New Year and the Spring Festival are much quieter and cheaper.',
    ticketHeading: 'Lift tickets',
    ticketBody:
      '2026-27 prices had not been published when we checked. In 2025-26 rack day tickets ran from 443 yuan on a Thaiwoo weekday to 800 yuan for a Wanlong holiday, and discounted prices through the resorts\' own channels were often half that. Duolemeidi, the smallest, charged 99 to 198 yuan.',
    accessHeading: 'Getting there',
    accessBody:
      'Take the Beijing-Zhangjiakou high-speed line from Beijing North or Qinghe to Taizicheng (for Genting, Thaiwoo and Wanlong) or Chongli (for Fulong), then a free resort shuttle. In 2025-26 coaches ran from both Beijing airports and Beijing South station for 80 yuan (100 from Daxing). By car it is about two to three hours.',
    linksHeading: 'More on Chongli',
    links: [
      { to: '/resort/wanlong/', label: 'Wanlong snow forecast and satellite snow cover', icon: 'camera' },
      { to: '/season-calendar/', label: 'Opening and closing dates', icon: 'calendar' },
    ],
    faq: [
      { q: 'When does Chongli open?', a: 'Wanlong usually opens first in late October, and the rest in November. In 2025-26 Wanlong opened on 21 October. 2026-27 dates had not been announced on 20 October 2026.' },
      { q: 'Is Chongli good for beginners?', a: 'Yes. Every big resort has beginner areas and ski schools, Thaiwoo has the highest share of beginner and intermediate runs of the big four (16 of 22), Fulong has a large kids\' area, and Wanlong is the most advanced-heavy.' },
      { q: 'Can I visit Chongli on 240-hour visa-free transit?', a: 'Yes, if you enter at Beijing: Beijing, Tianjin and Hebei are all permitted areas, and Chongli is in Hebei.' },
    ],
  },
  zh: {
    title: '崇礼滑雪场攻略 2026-27：雪场、雪票、高铁与行程',
    description:
      '北京周边的2022冬奥雪上赛区崇礼滑雪指南：主要雪场对比、2025-26雪季日期与票价、到太子城的高铁以及住宿选择。',
    keywords: '崇礼滑雪, 崇礼滑雪场, 太舞, 云顶滑雪公园, 万龙滑雪场, 富龙, 张家口滑雪, 北京滑雪',
    h1: '崇礼滑雪场：雪季、雪票、高铁与四天行程',
    lede:
      '河北张家口崇礼是北京可达范围内规模最大的滑雪场群，也是2022年冬奥会的雪上赛区。全区有9座雪场、219条雪道、总长178公里，从北京清河站乘高铁到太子城约一小时。',
    factsHeading: '基本数据',
    facts: [
      { label: '位置', value: '河北省张家口市崇礼区' },
      { label: '雪场', value: '9座雪场、219条雪道、178公里，16条国际雪联认证雪道' },
      { label: '主要雪场', value: '万龙32条；云顶32条+7条公园/赛道；富龙雪道数未公布' },
      { label: '雪季', value: '10月下旬至3月；万龙2025年10月21日开板' },
      { label: '从北京出发', value: '清河站乘高铁到太子城约1小时' },
    ],
    zonesHeading: '主要雪场',
    zones: [
      {
        name: '万龙',
        body: '滑雪爱好者的山：32条雪道约37公里，其中19条高级道，崇礼最长雪道长达4160米。山顶海拔2110米，落差约550米，通常最早开板。',
      },
      {
        name: '云顶',
        body: '冬奥单板和自由式场馆：32条编号雪道约28公里，另有7条公园和比赛赛道，山脚海拔约1700米，夜场开到21:30。',
      },
      {
        name: '太舞',
        body: '配套最完整的小镇，有酒店、酒吧、咖啡馆、温泉和滑冰场。2026-27雪道表共22条约16公里，山顶约2160米，落差约510米。',
      },
      {
        name: '富龙',
        body: '紧邻崇礼城区和崇礼站：33条雪道约25公里，30万平方米的地形公园和大型儿童区。',
      },
    ],
    snowHeading: '雪质与人流',
    snowBody:
      '崇礼寒冷干燥，雪道依赖人工造雪：冬奥申办时就有人批评这里缺乏稳定的天然降雪。结果是雪面坚实稳定，粉雪很少。周末和节假日北京雪友蜂拥而至，火车票售罄；元旦和春节以外的工作日人少很多，价格也便宜。',
    ticketHeading: '雪票',
    ticketBody:
      '我们核实时2026-27票价尚未公布。2025-26雪季全天票门市价从太舞平日443元到万龙节假日800元不等，通过雪场官方渠道购买的优惠价常常只有一半左右。规模最小的多乐美地为99至198元。',
    accessHeading: '交通',
    accessBody:
      '从北京北站或清河站乘京张高铁到太子城（去云顶、太舞、万龙）或崇礼站（去富龙），再换乘雪场免费班车。2025-26雪季有从北京两大机场和北京南站出发的大巴，票价80元（大兴机场100元）。自驾约两到三小时。',
    linksHeading: '更多崇礼信息',
    links: [
      { to: '/resort/wanlong/', label: '万龙降雪预报与卫星积雪图', icon: 'camera' },
      { to: '/season-calendar/', label: '开板与闭板日期', icon: 'calendar' },
    ],
    faq: [
      { q: '崇礼什么时候开板？', a: '万龙通常10月下旬最早开板，其他雪场在11月。2025-26雪季万龙于10月21日开板。截至2026年10月20日，2026-27雪季日期尚未公布。' },
      { q: '崇礼适合初学者吗？', a: '适合。各大雪场都有初级区和滑雪学校，四大雪场中太舞初中级道占比最高（22条中16条），富龙有大型儿童区，万龙高级道最多。' },
      { q: '能用240小时过境免签去崇礼吗？', a: '可以，只要从北京入境：北京、天津、河北都是允许区域，崇礼属于河北。' },
    ],
  },
  ko: {
    title: '충리 스키장 가이드 2026-27: 리조트·리프트권·기차',
    description:
      '베이징 근교 2022 동계올림픽 설상 경기장 충리 스키 가이드: 주요 리조트 비교, 2025-26 시즌 일정과 요금, 타이쯔청행 고속철, 숙소.',
    keywords: '충리 스키, 충리 스키장, 타이우, 겐팅 스노파크, 완룽 스키장, 푸룽, 장자커우 스키, 베이징 스키',
    h1: '충리 스키장: 시즌·리프트권·기차·4일 일정',
    lede:
      '허베이성 장자커우의 충리는 베이징에서 갈 수 있는 중국 최대의 스키장 밀집 지역이자 2022년 동계올림픽 설상 경기장입니다. 리조트 9곳, 코스 219개, 총 178km이며, 베이징 칭허역에서 고속철로 타이쯔청까지 약 1시간입니다.',
    factsHeading: '기본 정보',
    facts: [
      { label: '위치', value: '허베이성 장자커우시 충리구' },
      { label: '리조트', value: '9곳, 코스 219개, 178km, FIS 인증 코스 16개' },
      { label: '주요 리조트', value: '완룽 32개, 겐팅 32개+파크·경기 코스 7개, 푸룽은 코스 수 미공개' },
      { label: '시즌', value: '10월 말~3월, 완룽은 2025년 10월 21일 개장' },
      { label: '베이징에서', value: '칭허역~타이쯔청 고속철 약 1시간' },
    ],
    zonesHeading: '주요 리조트',
    zones: [
      {
        name: '완룽',
        body: '스키어를 위한 산. 코스 32개 약 37km 중 19개가 상급이고, 충리에서 가장 긴 4,160m 코스가 있습니다. 정상 2,110m, 표고차 약 550m, 보통 가장 먼저 개장합니다.',
      },
      {
        name: '겐팅(윈딩)',
        body: '올림픽 스노보드·프리스타일 경기장. 번호 코스 32개 약 28km에 파크·경기 코스 7개, 베이스 해발 약 1,700m, 야간 스키는 21:30까지.',
      },
      {
        name: '타이우',
        body: '가장 완성도 높은 베이스 마을로 호텔, 바, 카페, 온천, 아이스링크가 있습니다. 2026-27 표 기준 코스 22개 약 16km, 정상 약 2,160m, 표고차 약 510m.',
      },
      {
        name: '푸룽',
        body: '충리 시내와 충리역 바로 옆. 코스 33개 약 25km, 30만㎡ 지형 공원과 큰 키즈 구역.',
      },
    ],
    snowHeading: '눈과 혼잡도',
    snowBody:
      '충리는 춥고 건조해 슬로프가 인공 제설에 의존합니다. 올림픽 유치 때 안정적인 자연설이 부족하다는 비판이 있었습니다. 그래서 설면은 단단하고 일정하며 파우더는 드뭅니다. 주말과 공휴일에는 베이징 스키어가 몰리고 기차표가 매진되지만, 신정과 춘절을 뺀 평일은 훨씬 한산하고 저렴합니다.',
    ticketHeading: '리프트권',
    ticketBody:
      '확인 시점에 2026-27 요금은 발표되지 않았습니다. 2025-26 1일권 정가는 타이우 평일 443위안부터 완룽 공휴일 800위안까지였고, 리조트 공식 채널 할인가는 그 절반 정도인 경우가 많았습니다. 가장 작은 둬러메이디는 99~198위안이었습니다.',
    accessHeading: '가는 방법',
    accessBody:
      '베이징베이역이나 칭허역에서 징장 고속철로 타이쯔청(겐팅, 타이우, 완룽) 또는 충리역(푸룽)까지 간 뒤 리조트 무료 셔틀을 탑니다. 2025-26 시즌에는 베이징 두 공항과 베이징남역에서 80위안(다싱 공항 100위안) 버스가 있었습니다. 자동차로는 약 2~3시간입니다.',
    linksHeading: '충리 더 보기',
    links: [
      { to: '/resort/wanlong/', label: '완룽 강설 예보와 위성 적설 지도', icon: 'camera' },
      { to: '/season-calendar/', label: '개장·폐장 일정', icon: 'calendar' },
    ],
    faq: [
      { q: '충리는 언제 개장하나요?', a: '보통 완룽이 10월 말 가장 먼저, 나머지는 11월에 엽니다. 2025-26 시즌 완룽은 10월 21일 개장했습니다. 2026년 10월 20일 기준 2026-27 일정은 발표되지 않았습니다.' },
      { q: '충리는 초보자에게 괜찮나요?', a: '네. 큰 리조트마다 초급 구역과 스키 스쿨이 있고, 빅4 중 타이우의 초·중급 비중이 가장 높고(22개 중 16개), 푸룽에는 큰 키즈 구역이 있으며, 완룽은 상급 코스 비중이 가장 높습니다.' },
      { q: '240시간 경유 무비자로 충리에 갈 수 있나요?', a: '베이징으로 입국하면 가능합니다. 베이징·톈진·허베이가 모두 허용 지역이고 충리는 허베이에 있습니다.' },
    ],
  },
  ja: {
    title: '崇礼のスキー場ガイド 2026-27｜リゾート・リフト券・列車',
    description:
      '北京近郊、2022年冬季五輪の雪上会場・崇礼のスキーガイド。主要スキー場の比較、2025-26シーズンの日程と料金、太子城への高速鉄道、宿泊。',
    keywords: '崇礼 スキー, 崇礼 スキー場, 太舞, 雲頂スノーパーク, 万龍, 富竜, 張家口 スキー, 北京 スキー',
    h1: '崇礼のスキー場：シーズン・リフト券・列車・4日間プラン',
    lede:
      '河北省張家口市の崇礼は、北京から行ける中国最大のスキー場集積地で、2022年冬季五輪の雪上会場です。9つのスキー場、219本のコース、総延長178kmがあり、北京の清河駅から高速鉄道で太子城まで約1時間です。',
    factsHeading: '基本データ',
    facts: [
      { label: '所在地', value: '河北省張家口市崇礼区' },
      { label: 'スキー場', value: '9か所、219コース、178km、FIS公認コース16本' },
      { label: '主なスキー場', value: '万龍32本、雲頂32本＋パーク・競技コース7本、富竜はコース数非公表' },
      { label: 'シーズン', value: '10月下旬〜3月、万龍は2025年10月21日にオープン' },
      { label: '北京から', value: '清河駅〜太子城 高速鉄道で約1時間' },
    ],
    zonesHeading: '主なスキー場',
    zones: [
      {
        name: '万龍',
        body: '滑りたい人の山。32本・約37kmのうち19本が上級で、崇礼最長の4,160mのコースがあります。山頂2,110m、標高差約550m、例年最も早くオープン。',
      },
      {
        name: '雲頂（ゲンティン）',
        body: '五輪のスノーボード・フリースタイル会場。番号付きコース32本・約28kmに加えパーク・競技コース7本、ベースは標高約1,700m、ナイターは21:30まで。',
      },
      {
        name: '太舞',
        body: 'ベースタウンが最も充実し、ホテル、バー、カフェ、温泉、スケートリンクがあります。2026-27コース表で22本・約16km、山頂約2,160m、標高差約510m。',
      },
      {
        name: '富竜',
        body: '崇礼の町と崇礼駅のすぐそば。33本・約25km、30万㎡のテレインパークと大きなキッズエリア。',
      },
    ],
    snowHeading: '雪質と混雑',
    snowBody:
      '崇礼は寒く乾燥しており、ゲレンデは人工降雪に頼っています。五輪招致の際には安定した自然降雪がないと批判されました。そのため雪面は硬めで安定しており、パウダーは多くありません。週末と祝日は北京のスキーヤーで混み列車も売り切れますが、元日と春節以外の平日はずっと空いていて安くなります。',
    ticketHeading: 'リフト券',
    ticketBody:
      '確認時点で2026-27の料金は未発表でした。2025-26の1日券定価は太舞平日443元から万龍祝日800元まで、スキー場公式チャネルの割引価格はその半分ほどのことが多くありました。最も小さい多楽美地は99〜198元でした。',
    accessHeading: 'アクセス',
    accessBody:
      '北京北駅か清河駅から京張高速鉄道で太子城（雲頂・太舞・万龍）か崇礼駅（富竜）へ行き、スキー場の無料シャトルに乗り換えます。2025-26シーズンは北京の2つの空港と北京南駅から80元（大興空港は100元）のバスがありました。車なら約2〜3時間です。',
    linksHeading: '崇礼をさらに詳しく',
    links: [
      { to: '/resort/wanlong/', label: '万龍の降雪予報と衛星積雪画像', icon: 'camera' },
      { to: '/season-calendar/', label: 'オープン・クローズ日程', icon: 'calendar' },
    ],
    faq: [
      { q: '崇礼はいつオープンしますか？', a: '例年、万龍が10月下旬に最も早く、ほかは11月にオープンします。2025-26シーズンの万龍は10月21日オープン。2026年10月20日時点で2026-27の日程は未発表です。' },
      { q: '崇礼は初心者向きですか？', a: 'はい。大きなスキー場にはどこも初心者エリアとスクールがあり、4大スキー場では太舞の初中級コースの割合が最も高く（22本中16本）、富竜には大きなキッズエリアがあり、万龍は上級コースが最も多いです。' },
      { q: '240時間トランジット免除で崇礼に行けますか？', a: '北京から入国すれば行けます。北京・天津・河北はいずれも指定地域で、崇礼は河北にあります。' },
    ],
  },
};

// ---------------------------------------------------------------------------
// /chongli/travel-guide/ (Japan: COPY in src/pages/FuranoGuidePage.tsx)
// ---------------------------------------------------------------------------

/** Icon keys used by the travel guide sections. */
export type ChongliGuideIcon = 'plane' | 'hotel' | 'utensils' | 'calendar';

export interface ChongliTravelGuideCopy {
  title: string;
  description: string;
  keywords: string;
  h1: string;
  lede: string;
  sections: { icon: ChongliGuideIcon; heading: string; body: string[] }[];
  linksHeading: string;
  links: { to: string; label: string }[];
}

export const CHONGLI_TRAVEL_GUIDE: Record<CnLang, ChongliTravelGuideCopy> = {
  en: {
    title: 'Chongli travel guide: trains, hotels, food and a 4-day plan',
    description:
      'How to get from Beijing to Chongli by high-speed train or coach, which resort town to stay in, what to eat, and a practical four-day Chongli ski itinerary.',
    keywords: 'chongli travel guide, beijing to chongli train, taizicheng station, where to stay chongli, chongli itinerary',
    h1: 'Chongli ski trip travel guide',
    lede: 'Chongli works best as a train trip from Beijing. Here is the practical order: getting there, where to sleep, eating, and a four-day plan.',
    sections: [
      {
        icon: 'plane',
        heading: 'Getting there: the train to Taizicheng',
        body: [
          'Fly into Beijing Capital or Daxing, then take the Beijing-Zhangjiakou high-speed line from Beijing North or Qinghe. Qinghe links to Metro Line 13 and gets you to Taizicheng in about an hour; Beijing North takes about 1 hour 25 minutes. Get off at Taizicheng for Genting, Thaiwoo and Wanlong, or ride to the terminus at Chongli for Fulong. Free resort shuttles meet trains at Taizicheng.',
          'Book on 12306 (English, foreign passports accepted) or Trip.com. Friday-evening and Sunday-afternoon trains sell out first. If you are bringing your own skis, 2025-26 had a 68-yuan snow-gear booking in the 12306 app for marked trains. In 2025-26, coaches also ran from both Beijing airports and Beijing South station for 80 yuan (Daxing 100).',
        ],
      },
      {
        icon: 'hotel',
        heading: 'Where to stay: pick the resort, then the hotel',
        body: [
          'Most visitors sleep at the resort they ski. Thaiwoo town has the widest choice, from a Hyatt Place and a Marriott Element to a youth hostel. Genting has the Genting Grand Hotel and apartments; Wanlong has the Longgong and Shuanglong hotels and several apartment buildings, mostly ski-in/ski-out. Fulong\'s hotels sit at the edge of Chongli town.',
          'Taizicheng Ice and Snow Town by the station is a neutral base if you want to ski several resorts by shuttle. Chongli town is the budget option, with everyday restaurants and a daily shuttle or taxi ride to the lifts.',
        ],
      },
      {
        icon: 'utensils',
        heading: 'Food: eat at the resort, explore in town',
        body: [
          'Resort villages handle lunch and dinner: Wanlong has several themed restaurants and bars, Thaiwoo\'s base has bars and cafés. Chongli town has ordinary local restaurants at lower prices. Pay with Alipay or WeChat Pay; carry some cash in case a QR code fails.',
          'English menus are rare outside the international hotels, so a translation app with camera mode is the most useful tool on the trip.',
        ],
      },
      {
        icon: 'calendar',
        heading: 'A four-day plan',
        body: [
          'Day 1: Morning train from Qinghe to Taizicheng, shuttle to Thaiwoo, rentals and an easy afternoon.',
          'Day 2: Thaiwoo in the morning, the ice rink or hot spring after the lifts close.',
          'Day 3: Shuttle to Wanlong for its long advanced runs, or Genting to ride the Olympic park; night skiing ran to 21:30 at both in 2025-26.',
          'Day 4: A last morning, then the afternoon train back; on a weekday the Snow Ruyi jump hill near Taizicheng is worth a look on the way.',
        ],
      },
    ],
    linksHeading: 'Related pages',
    links: [
      { to: '/chongli/', label: 'Chongli guide: resorts, snow and tickets' },
      { to: '/where-to-stay/chongli/', label: 'Where to stay in Chongli' },
      { to: '/china-travel-essentials/', label: 'Visas, payments and apps' },
    ],
  },
  zh: {
    title: '崇礼旅行指南：火车、酒店、吃饭与4天行程',
    description: '从北京乘高铁或大巴去崇礼、住在哪个雪场小镇、吃什么，以及实用的崇礼4天滑雪行程。',
    keywords: '崇礼旅行攻略, 北京到崇礼高铁, 太子城站, 崇礼住宿, 崇礼行程',
    h1: '崇礼滑雪旅行指南',
    lede: '去崇礼最好坐火车。按实际顺序介绍：交通、住宿、吃饭，以及4天行程。',
    sections: [
      {
        icon: 'plane',
        heading: '交通：坐高铁到太子城',
        body: [
          '飞抵北京首都或大兴机场后，从北京北站或清河站乘京张高铁。清河站可换乘地铁13号线，到太子城约一小时；北京北站约1小时25分。去云顶、太舞、万龙在太子城下车，去富龙坐到终点崇礼站。太子城站有雪场免费班车接车。',
          '在12306（有英文版，接受外国护照）或携程订票。周五晚和周日下午的车次最先售罄。自带雪具的话，2025-26雪季可在12306 App为带"雪"标识的车次预订雪具位，每套68元。2025-26雪季还有从北京两大机场和北京南站出发的大巴，80元（大兴100元）。',
        ],
      },
      {
        icon: 'hotel',
        heading: '住宿：先选雪场，再选酒店',
        body: [
          '多数人住在要滑的雪场。太舞小镇选择最多，从凯悦嘉轩、万豪源宿到青旅都有。云顶有云顶大酒店和公寓；万龙有龙宫酒店、双龙酒店和多栋公寓，大多可滑进滑出。富龙的酒店在崇礼城区边上。',
          '如果想坐班车轮流滑几座雪场，车站旁的太子城冰雪小镇是比较中立的落脚点。崇礼城区最省钱，有日常餐馆，每天坐班车或打车上山。',
        ],
      },
      {
        icon: 'utensils',
        heading: '吃饭：在雪场吃，去城里逛',
        body: [
          '雪场小镇可以解决午饭和晚饭：万龙有多家主题餐厅和酒吧，太舞山脚有酒吧和咖啡馆。崇礼城区有价格更低的本地餐馆。用支付宝或微信支付，备些现金以防扫码失败。',
          '国际酒店以外很少有英文菜单，带拍照翻译功能的翻译App是这趟旅行最有用的工具。',
        ],
      },
      {
        icon: 'calendar',
        heading: '4天行程',
        body: [
          '第1天：上午从清河乘高铁到太子城，坐班车到太舞，租好雪具，下午轻松滑。',
          '第2天：上午滑太舞，索道停运后去滑冰场或泡温泉。',
          '第3天：坐班车去万龙滑长距离高级道，或去云顶体验冬奥公园；2025-26雪季两地夜场都开到21:30。',
          '第4天：最后一个上午，下午乘火车返回；工作日可顺路看看太子城附近的雪如意跳台。',
        ],
      },
    ],
    linksHeading: '相关页面',
    links: [
      { to: '/chongli/', label: '崇礼指南：雪场、雪质与雪票' },
      { to: '/where-to-stay/chongli/', label: '崇礼住哪里' },
      { to: '/china-travel-essentials/', label: '签证、支付与App' },
    ],
  },
  ko: {
    title: '충리 여행 가이드: 기차·호텔·식사·4일 일정',
    description: '베이징에서 고속철이나 버스로 충리 가는 법, 어느 리조트 마을에 묵을지, 무엇을 먹을지, 실용적인 4일 스키 일정.',
    keywords: '충리 여행, 베이징 충리 기차, 타이쯔청역, 충리 숙소, 충리 일정',
    h1: '충리 스키 여행 가이드',
    lede: '충리는 베이징에서 기차로 가는 여행이 가장 잘 맞습니다. 교통, 숙소, 식사, 4일 일정 순서로 정리했습니다.',
    sections: [
      {
        icon: 'plane',
        heading: '가는 법: 타이쯔청행 고속철',
        body: [
          '베이징 서우두나 다싱 공항에 도착해 베이징베이역이나 칭허역에서 징장 고속철을 탑니다. 칭허역은 지하철 13호선과 연결되고 타이쯔청까지 약 1시간, 베이징베이역은 약 1시간 25분입니다. 겐팅·타이우·완룽은 타이쯔청, 푸룽은 종점 충리역에서 내립니다. 타이쯔청역에는 리조트 무료 셔틀이 열차를 기다립니다.',
          '12306(영어, 외국 여권 가능)이나 트립닷컴에서 예매하세요. 금요일 저녁과 일요일 오후 열차가 먼저 매진됩니다. 장비를 가져간다면 2025-26 시즌에는 12306 앱에서 표시된 열차에 세트당 68위안으로 장비 공간을 예약할 수 있었습니다. 2025-26 시즌에는 베이징 두 공항과 베이징남역에서 80위안(다싱 100위안) 버스도 있었습니다.',
        ],
      },
      {
        icon: 'hotel',
        heading: '숙소: 리조트를 먼저, 호텔은 그다음',
        body: [
          '대부분 타는 리조트에 묵습니다. 타이우 마을은 하얏트 플레이스, 메리어트 엘리먼트부터 유스호스텔까지 선택지가 가장 많습니다. 겐팅에는 겐팅 그랜드 호텔과 아파트, 완룽에는 룽궁 호텔, 솽룽 호텔과 여러 아파트가 있으며 대부분 스키인·스키아웃입니다. 푸룽 호텔은 충리 시내 가장자리에 있습니다.',
          '셔틀로 여러 리조트를 돌고 싶다면 역 옆 타이쯔청 빙설 타운이 무난한 거점입니다. 충리 시내는 가장 저렴하고 일상적인 식당이 있으며, 매일 셔틀이나 택시로 리프트까지 갑니다.',
        ],
      },
      {
        icon: 'utensils',
        heading: '식사: 리조트에서 먹고 시내는 구경',
        body: [
          '점심과 저녁은 리조트 마을에서 해결됩니다. 완룽에는 테마 레스토랑과 바가 여럿 있고, 타이우 베이스에는 바와 카페가 있습니다. 충리 시내에는 더 저렴한 현지 식당이 있습니다. 결제는 알리페이나 위챗페이로 하고, QR 결제가 안 될 때를 대비해 현금을 조금 챙기세요.',
          '국제 호텔 밖에서는 영어 메뉴가 드물어서 카메라 번역 기능이 있는 번역 앱이 가장 유용합니다.',
        ],
      },
      {
        icon: 'calendar',
        heading: '4일 일정',
        body: [
          '1일차: 오전 칭허에서 타이쯔청행 고속철, 셔틀로 타이우, 장비 렌탈 후 가볍게 오후 스키.',
          '2일차: 오전 타이우, 리프트 마감 후 아이스링크나 온천.',
          '3일차: 셔틀로 완룽에 가서 긴 상급 코스, 또는 겐팅의 올림픽 파크. 2025-26 시즌 두 곳 모두 야간 스키가 21:30까지였습니다.',
          '4일차: 마지막 오전 스키 후 오후 기차로 복귀. 평일이라면 가는 길에 타이쯔청 근처 쉐루이 점프대를 볼 만합니다.',
        ],
      },
    ],
    linksHeading: '관련 페이지',
    links: [
      { to: '/chongli/', label: '충리 가이드: 리조트·눈·리프트권' },
      { to: '/where-to-stay/chongli/', label: '충리 숙소 가이드' },
      { to: '/china-travel-essentials/', label: '비자·결제·앱' },
    ],
  },
  ja: {
    title: '崇礼 旅行ガイド｜列車・ホテル・食事・4日間プラン',
    description: '北京から高速鉄道やバスで崇礼へ行く方法、どのスキー場の町に泊まるか、食事、実用的な4日間のスキー日程。',
    keywords: '崇礼 旅行, 北京 崇礼 列車, 太子城駅, 崇礼 宿泊, 崇礼 日程',
    h1: '崇礼スキー旅行ガイド',
    lede: '崇礼は北京から列車で行くのがいちばんです。移動、宿、食事、4日間の日程の順にまとめます。',
    sections: [
      {
        icon: 'plane',
        heading: 'アクセス：太子城行きの高速鉄道',
        body: [
          '北京首都空港か大興空港に着いたら、北京北駅か清河駅から京張高速鉄道に乗ります。清河駅は地下鉄13号線と接続し、太子城まで約1時間、北京北駅からは約1時間25分。雲頂・太舞・万龍は太子城、富竜は終点の崇礼駅で降ります。太子城駅ではスキー場の無料シャトルが列車を待っています。',
          '予約は12306（英語対応、外国旅券可）かTrip.comで。金曜夜と日曜午後の列車から売り切れます。自分の板を持っていくなら、2025-26シーズンは12306アプリで対象列車に1セット68元で用具スペースを予約できました。2025-26シーズンは北京の2つの空港と北京南駅から80元（大興は100元）のバスもありました。',
        ],
      },
      {
        icon: 'hotel',
        heading: '宿：まずスキー場、次にホテル',
        body: [
          'ほとんどの人は滑るスキー場に泊まります。太舞の町はハイアット・プレイス、マリオットのエレメントからユースホステルまで最も選択肢が多いです。雲頂には雲頂大酒店とアパートメント、万龍には竜宮酒店、双竜酒店と複数のアパートメントがあり、多くがスキーイン・スキーアウトです。富竜のホテルは崇礼の町外れにあります。',
          'シャトルで複数のスキー場を回りたいなら、駅のそばの太子城氷雪タウンが中立的な拠点です。崇礼の町は最も安く、普段使いの食堂があり、毎日シャトルかタクシーでリフトへ向かいます。',
        ],
      },
      {
        icon: 'utensils',
        heading: '食事：スキー場で食べ、町は散策',
        body: [
          '昼食と夕食はスキー場の町で済みます。万龍にはテーマレストランやバーがいくつもあり、太舞のベースにはバーとカフェがあります。崇礼の町にはもっと安い地元の食堂があります。支払いはAlipayかWeChat Payで、QR決済が通らないときのために現金も少し持ちましょう。',
          '国際ホテル以外では英語メニューはまれなので、カメラ翻訳機能のある翻訳アプリが旅でいちばん役立ちます。',
        ],
      },
      {
        icon: 'calendar',
        heading: '4日間のモデル日程',
        body: [
          '1日目：午前に清河から太子城へ高速鉄道、シャトルで太舞へ。レンタルを済ませて午後は軽く滑る。',
          '2日目：午前は太舞、リフト終了後はスケートリンクか温泉。',
          '3日目：シャトルで万龍へ行き長い上級コース、または雲頂で五輪パーク。2025-26シーズンはどちらもナイターが21:30まででした。',
          '4日目：最後の午前を滑り、午後の列車で戻る。平日なら途中で太子城近くの雪如意のジャンプ台を見ておく価値があります。',
        ],
      },
    ],
    linksHeading: '関連ページ',
    links: [
      { to: '/chongli/', label: '崇礼ガイド（スキー場・雪・リフト券）' },
      { to: '/where-to-stay/chongli/', label: '崇礼の宿泊エリア' },
      { to: '/china-travel-essentials/', label: 'ビザ・決済・アプリ' },
    ],
  },
};

// ---------------------------------------------------------------------------
// /chongli/faq/ (Japan: COPY in src/pages/FuranoFaqPage.tsx)
// ---------------------------------------------------------------------------

export interface ChongliFaqCopy {
  title: string;
  description: string;
  keywords: string;
  h1: string;
  lede: string;
  faq: { q: string; a: string }[];
}

export const CHONGLI_FAQ: Record<CnLang, ChongliFaqCopy> = {
  en: {
    title: 'Chongli FAQ: snow, crowds, tickets and getting there',
    description: 'Chongli answered: snow quality, crowds, 2025-26 lift ticket prices, which resort to pick, visas, payment, English, rentals and the train from Beijing.',
    keywords: 'chongli faq, chongli snow, chongli ski ticket price, thaiwoo vs wanlong, beijing to chongli',
    h1: 'Chongli ski FAQ',
    lede: 'The questions people ask before booking a Chongli ski trip.',
    faq: [
      { q: 'How good is the snow in Chongli?', a: 'Consistent rather than deep. Chongli is cold and dry, so the slopes run mostly on machine-made snow that stays firm all day. For powder, look at Altay in Xinjiang.' },
      { q: 'When does the season run?', a: 'Late October to late March, with Wanlong usually open first. It opened on 21 October 2025 and 23 October 2026; the other big Chongli resorts are expected in November.' },
      { q: 'How much are lift tickets?', a: '2026-27 prices were not out yet. In 2025-26 rack day prices ran from 443 yuan (Thaiwoo weekday) to 800 yuan (Wanlong holiday), with discounted prices through official channels from about 250 to 700 yuan. Duolemeidi charged 99 to 198 yuan.' },
      { q: 'Which resort should I choose?', a: 'Wanlong for advanced skiers, Genting for the Olympic park and freestyle terrain, Thaiwoo for the best village, Fulong for families and easy station access.' },
      { q: 'Is it crowded?', a: 'Weekends and holidays are busy with Beijing skiers, and the New Year and Spring Festival weeks are the busiest and most expensive. Weekdays are much quieter.' },
      { q: 'How do I get there from Beijing?', a: 'High-speed train from Qinghe (about 1 hour) or Beijing North (about 1 hour 25 minutes) to Taizicheng or Chongli, then a free resort shuttle. In 2025-26 coaches also ran from both airports for 80 to 100 yuan.' },
      { q: 'Do I need a visa?', a: 'Many nationalities can use 30-day visa-free entry until 31 December 2026, and 240-hour transit covers Chongli if you enter at Beijing. US citizens need a visa or the transit route.' },
      { q: 'Can I pay with a foreign card?', a: 'International hotels take cards. For everything else, link your card to Alipay or WeChat Pay before you leave. WeChat Pay charges 3% on foreign-card payments over 200 yuan.' },
      { q: 'Is English spoken?', a: 'At international hotels, yes; elsewhere expect Chinese. Ask the ski school about English-speaking instructors in advance over WeChat or email.' },
      { q: 'Can I rent gear?', a: 'Yes, at every major resort. Some sell lift-plus-rental tickets; Thaiwoo took a 2,000-yuan deposit on rented gear in 2025-26.' },
      { q: 'Is there night skiing?', a: 'Yes. In 2025-26 Wanlong and Genting ran 18:30 to 21:30 and Fulong 17:30 to 21:30, on separate tickets.' },
      { q: 'What is there to do off the slopes?', a: 'Hot springs at Thaiwoo, Wanlong and Fulong, the ice rink in Thaiwoo town, and the Olympic sites around Taizicheng, including the Snow Ruyi ski jump.' },
    ],
  },
  zh: {
    title: '崇礼滑雪常见问题：雪质、人流、雪票与交通',
    description: '崇礼常见问题：雪质、人流、2025-26雪票价格、选哪个雪场、签证、支付、英语、租赁和从北京出发的火车。',
    keywords: '崇礼常见问题, 崇礼雪质, 崇礼雪票价格, 太舞和万龙哪个好, 北京到崇礼',
    h1: '崇礼滑雪常见问题',
    lede: '预订崇礼滑雪之旅前大家最常问的问题。',
    faq: [
      { q: '崇礼的雪怎么样？', a: '稳定而不深厚。崇礼寒冷干燥，雪道以人工造雪为主，全天都比较硬实。想滑粉雪请考虑新疆阿勒泰。' },
      { q: '雪季从什么时候到什么时候？', a: '10月下旬到3月下旬，万龙通常最早开。2025年10月21日和2026年10月23日开板，崇礼其他大雪场预计11月开板。' },
      { q: '雪票多少钱？', a: '2026-27票价尚未公布。2025-26全天票门市价从443元（太舞平日）到800元（万龙节假日），官方渠道优惠价约250至700元。多乐美地为99至198元。' },
      { q: '该选哪个雪场？', a: '高级滑手选万龙，想体验冬奥公园和自由式地形选云顶，想要最好的小镇选太舞，带孩子或想离车站近选富龙。' },
      { q: '人多吗？', a: '周末和节假日北京雪友很多，元旦和春节期间人最多、价格最高。工作日要安静得多。' },
      { q: '从北京怎么去？', a: '从清河站（约1小时）或北京北站（约1小时25分）乘高铁到太子城或崇礼，再坐雪场免费班车。2025-26雪季还有从两大机场出发的大巴，80至100元。' },
      { q: '需要签证吗？', a: '许多国家的公民可使用30天单方面免签，有效期至2026年12月31日；从北京入境时，240小时过境免签也覆盖崇礼。美国公民需要签证或使用过境免签。' },
      { q: '能用外国银行卡付款吗？', a: '国际酒店可以刷卡。其他场合请在出发前把银行卡绑定到支付宝或微信支付。微信支付对超过200元的外卡交易收取3%手续费。' },
      { q: '说英语行吗？', a: '国际酒店可以；其他地方基本说中文。需要英语教练请提前通过微信或邮件向滑雪学校确认。' },
      { q: '能租雪具吗？', a: '各大雪场都可以。部分雪场出售含雪具的套票；2025-26雪季太舞租雪具押金2000元。' },
      { q: '有夜场吗？', a: '有。2025-26雪季万龙和云顶18:30至21:30，富龙17:30至21:30，夜场单独售票。' },
      { q: '雪道之外能做什么？', a: '太舞、万龙和富龙有温泉，太舞小镇有滑冰场，太子城周边有冬奥场馆，包括雪如意跳台。' },
    ],
  },
  ko: {
    title: '충리 스키 FAQ: 눈·혼잡·리프트권·교통',
    description: '충리 질문 모음: 설질, 혼잡도, 2025-26 리프트권 요금, 리조트 고르기, 비자, 결제, 영어, 렌탈, 베이징에서 가는 기차.',
    keywords: '충리 FAQ, 충리 설질, 충리 리프트권 가격, 타이우 완룽 비교, 베이징 충리',
    h1: '충리 스키 자주 묻는 질문',
    lede: '충리 스키 여행을 예약하기 전에 많이 묻는 질문입니다.',
    faq: [
      { q: '충리 눈은 어떤가요?', a: '깊기보다는 일정합니다. 춥고 건조해 슬로프는 주로 인공설이고 하루 종일 단단합니다. 파우더를 원하면 신장 알타이를 보세요.' },
      { q: '시즌은 언제인가요?', a: '10월 말부터 3월 말까지이며 보통 완룽이 가장 먼저 엽니다. 2025년 10월 21일, 2026년 10월 23일에 개장했고 충리의 다른 큰 리조트는 11월로 예상됩니다.' },
      { q: '리프트권은 얼마인가요?', a: '2026-27 요금은 아직입니다. 2025-26 1일권 정가는 443위안(타이우 평일)부터 800위안(완룽 공휴일)까지, 공식 채널 할인가는 약 250~700위안이었습니다. 둬러메이디는 99~198위안이었습니다.' },
      { q: '어느 리조트를 골라야 하나요?', a: '상급자는 완룽, 올림픽 파크와 프리스타일 지형은 겐팅, 마을 분위기는 타이우, 가족과 역 접근성은 푸룽입니다.' },
      { q: '붐비나요?', a: '주말과 공휴일에는 베이징 스키어로 붐비고, 신정과 춘절 주간이 가장 붐비고 비쌉니다. 평일은 훨씬 한산합니다.' },
      { q: '베이징에서 어떻게 가나요?', a: '칭허역(약 1시간)이나 베이징베이역(약 1시간 25분)에서 고속철로 타이쯔청이나 충리까지 가서 리조트 무료 셔틀을 탑니다. 2025-26 시즌에는 두 공항에서 80~100위안 버스도 있었습니다.' },
      { q: '비자가 필요한가요?', a: '많은 국적이 2026년 12월 31일까지 30일 무비자 입국을 쓸 수 있고, 베이징으로 입국하면 240시간 경유 무비자도 충리를 포함합니다. 미국 국민은 비자나 경유 제도가 필요합니다.' },
      { q: '해외 카드로 결제할 수 있나요?', a: '국제 호텔은 카드를 받습니다. 그 외에는 출국 전에 알리페이나 위챗페이에 카드를 등록하세요. 위챗페이는 200위안 초과 해외 카드 결제에 3% 수수료가 있습니다.' },
      { q: '영어가 통하나요?', a: '국제 호텔에서는 통하지만 그 밖에서는 중국어라고 보세요. 영어 강사는 미리 위챗이나 이메일로 스키 스쿨에 문의하세요.' },
      { q: '장비를 빌릴 수 있나요?', a: '네, 주요 리조트 어디서나 가능합니다. 리프트+렌탈 티켓을 파는 곳도 있고, 2025-26 시즌 타이우는 렌탈 보증금 2,000위안을 받았습니다.' },
      { q: '야간 스키가 있나요?', a: '네. 2025-26 시즌 완룽과 겐팅은 18:30~21:30, 푸룽은 17:30~21:30이었고 별도 티켓입니다.' },
      { q: '스키 외에 할 것은?', a: '타이우·완룽·푸룽의 온천, 타이우 마을 아이스링크, 쉐루이 점프대를 포함한 타이쯔청 주변 올림픽 시설이 있습니다.' },
    ],
  },
  ja: {
    title: '崇礼スキー よくある質問｜雪・混雑・リフト券・アクセス',
    description: '崇礼の雪質、混雑、2025-26リフト券料金、スキー場の選び方、ビザ、支払い、英語、レンタル、北京からの列車についての質問に答えます。',
    keywords: '崇礼 よくある質問, 崇礼 雪質, 崇礼 リフト券 料金, 太舞 万龍 比較, 北京 崇礼',
    h1: '崇礼スキー よくある質問',
    lede: '崇礼のスキー旅行を予約する前によく聞かれる質問をまとめました。',
    faq: [
      { q: '崇礼の雪はどうですか？', a: '深さより安定感。崇礼は寒く乾燥しているため、ゲレンデは主に人工雪で一日中硬めです。パウダーなら新疆のアルタイを検討してください。' },
      { q: 'シーズンはいつですか？', a: '10月下旬から3月下旬で、例年万龍が最も早く開きます。2025年10月21日と2026年10月23日にオープンし、崇礼のほかの大型スキー場は11月の見込みです。' },
      { q: 'リフト券はいくらですか？', a: '2026-27の料金はまだです。2025-26の1日券定価は443元（太舞平日）から800元（万龍祝日）まで、公式チャネルの割引価格は約250〜700元でした。多楽美地は99〜198元でした。' },
      { q: 'どのスキー場を選べばいいですか？', a: '上級者は万龍、五輪パークとフリースタイルは雲頂、町の雰囲気は太舞、家族連れと駅からの近さは富竜です。' },
      { q: '混みますか？', a: '週末と祝日は北京のスキーヤーで混み、元日と春節の週が最も混んで高くなります。平日はずっと空いています。' },
      { q: '北京からどう行きますか？', a: '清河駅（約1時間）か北京北駅（約1時間25分）から高速鉄道で太子城か崇礼へ行き、スキー場の無料シャトルに乗ります。2025-26シーズンは両空港から80〜100元のバスもありました。' },
      { q: 'ビザは必要ですか？', a: '多くの国籍は2026年12月31日まで30日のビザ免除入国が使え、北京から入国すれば240時間トランジット免除でも崇礼に行けます。米国籍はビザかトランジット免除が必要です。' },
      { q: '海外カードで払えますか？', a: '国際ホテルではカードが使えます。それ以外は出発前にAlipayかWeChat Payにカードを登録しましょう。WeChat Payは200元を超える海外カード決済に3%の手数料がかかります。' },
      { q: '英語は通じますか？', a: '国際ホテルでは通じますが、それ以外は中国語と考えてください。英語のインストラクターは事前にWeChatかメールでスクールに確認を。' },
      { q: 'レンタルはできますか？', a: 'はい、主要スキー場ならどこでも。リフト＋レンタル券を売るところもあり、2025-26シーズンの太舞はレンタル保証金2,000元でした。' },
      { q: 'ナイターはありますか？', a: 'あります。2025-26シーズンは万龍と雲頂が18:30〜21:30、富竜が17:30〜21:30で、別券です。' },
      { q: 'スキー以外に何ができますか？', a: '太舞・万龍・富竜の温泉、太舞の町のスケートリンク、雪如意のジャンプ台を含む太子城周辺の五輪施設があります。' },
    ],
  },
};
